"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ProductForClient } from "@/lib/shared/types/interfaces";

const MAX_COMPARE_ITEMS = 4;

interface ComparingProductsStore {
  products: ProductForClient[];
  maxItems: number;
  addProduct: (product: ProductForClient) => void;
  removeProduct: (product: ProductForClient) => void;
  removeById: (productId: string) => void;
  isCompared: (productId: string) => boolean;
  canAddMore: () => boolean;
  number: () => number;
  clear: () => void;
}

/**
 * Product comparison store
 * Manages products being compared
 */
const useComparingProductsStore = create<ComparingProductsStore>()(
  persist(
    (set, get) => ({
      products: [],
      maxItems: MAX_COMPARE_ITEMS,
      number: () => get().products.length,
      isCompared: (productId: string) =>
        get().products.some((product) => String(product.id) === productId),
      canAddMore: () => get().products.length < get().maxItems,
      addProduct: (product: ProductForClient) =>
        set((state) => {
          const currentProducts = state.products;
          const productId = String(product.id);

          if (currentProducts.some((currentProduct) => String(currentProduct.id) === productId)) {
            return state;
          }

          if (currentProducts.length >= state.maxItems) {
            return state;
          }

          return { products: [...currentProducts, product] };
        }),
      removeById: (productId: string) =>
        set((state) => ({
          products: state.products.filter((product) => String(product.id) !== productId),
        })),
      removeProduct: (product: ProductForClient) =>
        set((state) => ({
          products: state.products.filter((currentProduct) => currentProduct.id !== product.id),
        })),
      clear: () => set({ products: [] }),
    }),
    {
      name: "compare-products",
      partialize: (state) => ({ products: state.products }),
    },
  ),
);

export default useComparingProductsStore;
